import { invoke } from "@tauri-apps/api/core";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { atom } from "jotai";

import { env } from "../env";
import type {
	BranchInfo,
	GitAheadBehind,
	GitDiff,
	GitMergeAnalysis,
	GitRemote,
	GitRepoHistory,
	GitRepoStatus,
	GitShow,
} from "./git.types";

// Combined result from commit_and_refresh Tauri command
export type CommitAndRefreshResult = {
	status: GitRepoStatus | undefined;
	history: GitRepoHistory;
	branches: BranchInfo[];
	describedCommitHash: string;
};

// Rust backend types (snake_case)
type RustGitRemote = {
	url: string;
	name: string;
};

type RustGitRepoHistory = {
	commits: {
		message: string;
		date: string;
		hash: string;
		author: string;
		parents: string[];
		/** JJ change ID (revision ID). Only present for JJ-backed repos. */
		change_id?: string;
		/** Whether this commit has unresolved conflicts (JJ-specific). */
		has_conflict?: boolean;
		/** Whether this commit is immutable (ancestor of trunk/tags/untracked remote bookmarks). */
		immutable?: boolean;
		/** Whether this commit is empty (no changes vs parent). */
		is_empty?: boolean;
	}[];
};

type RustGitRepoStatus = {
	ahead: number;
	behind: number;
	branch: string;
	commit: string;
	files: {
		from?: string;
		path?: string;
		index?: string;
		working_dir?: string;
	}[];
	is_clean: boolean;
	is_draft: boolean;
	is_conflicted: boolean;
	is_repo: boolean;
	changed: number;
	insertions: number;
	deletions: number;
};

type RustGitShow = {
	header: {
		author: {
			name: string;
			email: string;
		};
		commit_sha: string;
		parents: string[];
		committer: {
			name: string;
			email: string;
		};
		commit_time: string;
		commit_message: string;
	};
	contents: string[];
	original_diff: string;
	parsed_files: {
		type: "added" | "removed" | "renamed" | "modified";
		aPath: string;
		bPath: string;
		hunks: {
			aLineStart: number;
			aNumLines: number;
			bLineStart: number;
			bNumLines?: number;
			lines: {
				type: "insertion" | "deletion" | "context";
				content: string;
			}[];
		}[];
	}[];
};

/**
 * Transform Rust snake_case status to TS camelCase GitRepoStatus.
 * Returns undefined if the repo status indicates it's not a valid repo.
 */
const transformRustStatus = (rawStatus: RustGitRepoStatus): GitRepoStatus | undefined => {
	if (!rawStatus.is_repo) {
		return undefined;
	}

	return {
		isClean: rawStatus.is_clean && !rawStatus.is_conflicted,
		isDraft: rawStatus.is_draft,
		isConflicted: rawStatus.is_conflicted,
		ahead: rawStatus.ahead,
		behind: rawStatus.behind,
		branch: rawStatus.branch,
		commit: rawStatus.commit,
		files: rawStatus.files.map((file) => ({
			from: file.from,
			path: file.path || "",
			index: file.index || "",
			working_dir: file.working_dir || "",
		})),
		changed: rawStatus.changed,
		insertions: rawStatus.insertions,
		deletions: rawStatus.deletions,
	};
};

export type GitAPI = {
	// Core operations
	init: (params: { path: string }) => Promise<void>;
	initFromArchive: (params: {
		path: string;
		remoteUrl: string;
		token: string | undefined;
	}) => Promise<void>;
	commitAndRefresh: (params: {
		path: string;
		message: string;
	}) => Promise<CommitAndRefreshResult>;
	describe: (params: {
		path: string;
		message: string;
		revision?: string;
	}) => Promise<{ newCommitHash: string }>;
	clone: (params: {
		path: string;
		remoteUrl: string;
		token: string | undefined;
	}) => Promise<void>;
	diff: (params: { path: string }) => Promise<GitDiff>;
	diffRefs: (params: { path: string; aSide: string; bSide: string }) => Promise<GitDiff>;
	status: (params: { path: string }) => Promise<GitRepoStatus | undefined>;

	// Remote operations
	fetch: (params: {
		path: string;
		remote: string;
		branch: string;
		token: string | undefined;
	}) => Promise<void>;
	pull: (params: { path: string; token: string | undefined }) => Promise<void>;
	push: (params: {
		path: string;
		remote: string;
		branch: string;
		token: string | undefined;
		sourceCommit?: string;
	}) => Promise<void>;
	remoteAdd: (params: { path: string; remote: string; url: string }) => Promise<void>;
	remoteList: (params: { path: string }) => Promise<GitRemote[]>;

	// Branch operations
	branchNew: (params: { path: string; name: string; startPoint?: string }) => Promise<void>;
	branchList: (params: { path: string }) => Promise<BranchInfo[]>;
	branchSwitch: (params: { path: string; target: string }) => Promise<void>;
	branchDelete: (params: { path: string; name: string; force: boolean }) => Promise<void>;
	branchTrack: (params: {
		path: string;
		remote: string;
		localBranch: string;
		upstreamBranch: string;
		token: string | undefined;
	}) => Promise<void>;
	branchAnalyze: (params: {
		path: string;
		source: string;
		target: string;
	}) => Promise<GitMergeAnalysis>;
	branchAheadBehind: (params: {
		path: string;
		source: string;
		target: string;
	}) => Promise<GitAheadBehind>;
	moveCommit: (params: {
		path: string;
		commitHash: string;
		targetBranch: string;
	}) => Promise<{ newCommitHash: string }>;
	rebaseBookmark: (params: {
		path: string;
		localBranch: string;
		targetRef: string;
	}) => Promise<void>;
	syncBranch: (params: {
		path: string;
		sourceBranch: string;
		targetBranch: string;
	}) => Promise<string>;

	// Stash operations
	stashSave: (params: { path: string; message?: string }) => Promise<void>;
	stashPop: (params: { path: string; index: number }) => Promise<void>;

	// History operations
	repoHistory: (params: { path: string }) => Promise<GitRepoHistory>;
	listCommitsBetween: (params: {
		path: string;
		source: string;
		target: string;
	}) => Promise<GitRepoHistory>;
	show: (params: { path: string; commitHash: string }) => Promise<GitShow>;

	// Ref resolution
	resolveRef: (params: { path: string; refspec: string }) => Promise<string | null>;

	// Conflict resolution (JJ-specific)
	resolveConflict: (params: {
		path: string;
		commitHash: string;
		filePath: string;
		resolvedContent: string;
	}) => Promise<{ newCommitHash: string }>;
	restoreToParent: (params: {
		path: string;
		commitHash: string;
	}) => Promise<{ newCommitHash: string }>;
	undo: (params: { path: string; count: number }) => Promise<{ newCommitHash: string }>;

	// Config operations
	configGet: () => Promise<Record<string, string | number | null>>;
	configSet: (params: { newConfig: Record<string, string | number | null> }) => Promise<void>;
};

const tauriGitAPI = (): GitAPI => ({
	init: async (params) => {
		await invoke<void>("init_repo", params);
	},
	initFromArchive: async (params) => {
		await invoke<void>("init_from_archive", params);
	},
	clone: async (params) => {
		await invoke<void>("clone", params);
	},
	commitAndRefresh: async (params) => {
		const raw = await invoke<CommitAndRefreshResult>("commit_and_refresh", params);
		const rawStatus = raw.status as unknown as RustGitRepoStatus;
		return {
			...raw,
			status: transformRustStatus(rawStatus),
		};
	},
	describe: async (params) => {
		return invoke<{ newCommitHash: string }>("describe", {
			path: params.path,
			message: params.message,
			revision: params.revision ?? null,
		});
	},
	diff: async (params) => {
		return invoke<GitDiff>("diff", params);
	},
	diffRefs: async (params) => {
		return invoke<GitDiff>("diff_refs", params);
	},
	status: async (params) => {
		const rawStatus = await invoke<RustGitRepoStatus>("status", { path: params.path });
		return transformRustStatus(rawStatus);
	},
	fetch: async (params) => {
		await invoke<void>("fetch", params);
	},
	pull: async (params) => {
		await invoke<void>("pull_repo", params);
	},
	push: async (params) => {
		await invoke<void>("push", {
			path: params.path,
			remote: params.remote,
			branch: params.branch,
			token: params.token,
			sourceCommit: params.sourceCommit ?? null,
		});
	},
	remoteAdd: async (params) => {
		await invoke<void>("add_remote", params);
	},
	remoteList: async (params) => {
		return invoke<RustGitRemote[]>("list_remotes", params);
	},
	branchNew: async (params) => {
		await invoke<void>("new_branch", params);
	},
	branchList: async (params) => {
		return invoke<BranchInfo[]>("list_local_branches", params);
	},
	branchSwitch: async (params) => {
		await invoke<void>("switch_branch", params);
	},
	branchDelete: async (params) => {
		await invoke<void>("delete_branch", params);
	},
	branchTrack: async (params) => {
		const { path, remote, localBranch, upstreamBranch } = params;
		await invoke<void>("add_upstream_branch", { path, localBranch, remote, upstreamBranch });
	},
	branchAnalyze: async (params) => {
		return invoke<GitMergeAnalysis>("analyze_merge", params);
	},
	branchAheadBehind: async (params) => {
		return invoke<GitAheadBehind>("graph_ahead_behind", params);
	},
	moveCommit: async (params) => {
		return invoke<{ newCommitHash: string }>("move_commit", params);
	},
	rebaseBookmark: async (params) => {
		await invoke<void>("rebase_bookmark", params);
	},
	syncBranch: async (params) => {
		return invoke<string>("sync_branch", params);
	},
	stashSave: async (params) => {
		await invoke<void>("stash_save", params);
	},
	stashPop: async (params) => {
		await invoke<void>("stash_pop", params);
	},
	repoHistory: async (params) => {
		return invoke<RustGitRepoHistory>("repo_history", params);
	},
	listCommitsBetween: async (params) => {
		return invoke<RustGitRepoHistory>("list_commits_between", params);
	},
	show: async (params) => {
		const rawShow = await invoke<RustGitShow>("show", params);

		return {
			header: {
				prettyCommitSha: rawShow.header.commit_sha,
				authorName: rawShow.header.author.name,
				authorEmail: rawShow.header.author.email,
				prettyDate: rawShow.header.commit_time,
				timeSinceCommit: formatDistanceToNowStrict(parseISO(rawShow.header.commit_time)),
				commitMessage: rawShow.header.commit_message,
			},
			originalDiff: rawShow.original_diff,
			contents: rawShow.contents,
			parsedFiles: rawShow.parsed_files,
		};
	},
	resolveRef: async (params) => {
		return invoke<string | null>("resolve_ref", params);
	},
	resolveConflict: async (params) => {
		return invoke<{ newCommitHash: string }>("resolve_conflict", params);
	},
	restoreToParent: async (params) => {
		return invoke<{ newCommitHash: string }>("restore_to_parent", params);
	},
	undo: async (params) => {
		return invoke<{ newCommitHash: string }>("undo", params);
	},
	configGet: async () => {
		return invoke<Record<string, string | number | null>>("config", {});
	},
	configSet: async (params: { newConfig: Record<string, string | number | null> }) => {
		return invoke<void>("set_config", params);
	},
});

const noGitAPI: GitAPI = {
	init: () => Promise.reject(new Error("NO_FILESYSTEM")),
	initFromArchive: () => Promise.reject(new Error("NO_FILESYSTEM")),
	clone: () => Promise.reject(new Error("NO_FILESYSTEM")),
	commitAndRefresh: () =>
		Promise.reject(new Error("NO_FILESYSTEM")) as Promise<CommitAndRefreshResult>,
	describe: () =>
		Promise.reject(new Error("NO_FILESYSTEM")) as Promise<{ newCommitHash: string }>,
	diff: () => Promise.reject(new Error("NO_FILESYSTEM")) as Promise<GitDiff>,
	diffRefs: () => Promise.reject(new Error("NO_FILESYSTEM")) as Promise<GitDiff>,
	status: () => Promise.reject(new Error("NO_FILESYSTEM")),
	fetch: () => Promise.reject(new Error("NO_FILESYSTEM")),
	pull: () => Promise.reject(new Error("NO_FILESYSTEM")),
	push: () => Promise.reject(new Error("NO_FILESYSTEM")),
	remoteAdd: () => Promise.reject(new Error("NO_FILESYSTEM")),
	remoteList: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchNew: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchList: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchSwitch: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchDelete: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchTrack: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchAnalyze: () => Promise.reject(new Error("NO_FILESYSTEM")),
	branchAheadBehind: () => Promise.reject(new Error("NO_FILESYSTEM")),
	moveCommit: () => Promise.reject(new Error("NO_FILESYSTEM")),
	rebaseBookmark: () => Promise.reject(new Error("NO_FILESYSTEM")),
	syncBranch: () => Promise.reject(new Error("NO_FILESYSTEM")),
	stashSave: () => Promise.reject(new Error("NO_FILESYSTEM")),
	stashPop: () => Promise.reject(new Error("NO_FILESYSTEM")),
	repoHistory: () => Promise.reject(new Error("NO_FILESYSTEM")),
	listCommitsBetween: () => Promise.reject(new Error("NO_FILESYSTEM")),
	show: () => Promise.reject(new Error("NO_FILESYSTEM")),
	resolveRef: () => Promise.resolve(null),
	resolveConflict: () => Promise.reject(new Error("NO_FILESYSTEM")),
	restoreToParent: () => Promise.reject(new Error("NO_FILESYSTEM")),
	undo: () => Promise.reject(new Error("NO_FILESYSTEM")),
	configGet: () => Promise.reject(new Error("NO_FILESYSTEM")),
	configSet: () => Promise.reject(new Error("NO_FILESYSTEM")),
};

// Git API atom
export const $git = atom((get) => {
	if (env.isDesktopApp) {
		return tauriGitAPI();
	}

	return noGitAPI;
});
$git.debugLabel = "git";
